import ActionRequiredProvider from '@contexts/ActionRequired/ActionRequiredContext';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  ActionDecisionMethod,
  ActionPath,
} from '@contexts/ActionRequired/services/actionRequiredService';
import { getExamTimeInfo } from '@components/Banner/helpers';
import { useIsTeacher } from '@reducers/user/hooks';
import {
  useGetLearnosityExamTestSessions,
  useGetTestSessionCompletionStatus,
} from '../hooks';
import { ILEExamSession, TestletStatus, TestSessionCompletionStatus } from '../types';

type ActionRequiredContextType = {
  completionStatus: TestSessionCompletionStatus[];
  testSessions: ILEExamSession[];
  isTeacher: boolean;
};
type Props = {
  children: React.ReactNode;
};
const customDecisionMethods: ActionDecisionMethod<ActionRequiredContextType>[] = [
  {
    name: 'need to unlock testlet',
    method: (ctx: ActionRequiredContextType) => {
      const { testSessions, isTeacher } = ctx;
      if (!isTeacher || !testSessions) {
        return [];
      }
      return testSessions?.flatMap((testSession) =>
        testSession.testlets?.reduce((acc, t) => {
          const { hasStarted, isWithinLimit } = getExamTimeInfo(testSession.start_date);
          const shouldUnlock =
            t.status === TestletStatus.NOT_STARTED && hasStarted && isWithinLimit;
          if (!shouldUnlock) {
            return acc;
          }
          return [...acc, ['LE', t.exam_session, t.id]];
        }, [] as ActionPath[]),
      );
    },
  },
  {
    name: 'need to take testlet',
    method: (ctx: ActionRequiredContextType) => {
      const { testSessions, isTeacher, completionStatus } = ctx;
      if (isTeacher || !completionStatus) {
        return [];
      }
      const testletCompletionStatus = completionStatus?.reduce((acc, c) => {
        acc[c.testlet_id] = c.is_complete;
        return acc;
      }, {} as Record<number, boolean>);
      const actionsPaths = testSessions?.flatMap((testSession) =>
        testSession.testlets
          .filter((t) => {
            const isUnlocked = t.status === TestletStatus.UNLOCKED;
            const isNotComplete = !testletCompletionStatus[t.id];
            return isUnlocked && isNotComplete;
          })
          .map((t) => ['LE', t.exam_session, t.id]),
      );
      return actionsPaths;
    },
  },
];
const LEActionRequired = ({ children }: Props) => {
  const { sectionId } = useParams();
  const { data: testSessions } = useGetLearnosityExamTestSessions();
  const { data: completionStatus } = useGetTestSessionCompletionStatus(sectionId);
  const isTeacher = useIsTeacher();
  const ctx = useMemo(
    () => ({
      completionStatus,
      testSessions,
      isTeacher,
    }),
    [completionStatus, testSessions, isTeacher],
  );

  return (
    <ActionRequiredProvider context={ctx} customDecisionMethods={customDecisionMethods}>
      {children}
    </ActionRequiredProvider>
  );
};

export default LEActionRequired;
